import { useCallback, useEffect, useMemo, useRef } from "react";

export const useTimeout = <T>(callback: (args: T) => void, delay = 0): [(args: T) => void] => {
    const timeoutRef = useRef<number>();
    const callbackRef = useRef(callback);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        return () => {
            window.clearTimeout(timeoutRef.current);
        };
    }, []);

    const memoizedCallback = useCallback(
        (args: T) => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = window.setTimeout(() => {
                timeoutRef.current = undefined;
                callbackRef.current(args);
            }, delay);
        },
        [delay, timeoutRef, callbackRef],
    );

    return useMemo(() => [memoizedCallback], [memoizedCallback]);
};
